import type { ChatInfo, MessageWithMetadata } from './index';

const CHAT_MESSAGES_KEY = 'chat-messages';
const CHAT_LIST_KEY = 'chat-list';

export const cacheKey = (chatId: string) => `chat-messages-${chatId}`;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const failSilently = (fn: Function) => {
  try {
    return fn();
  } catch (e) {
    // Do nothing
    return undefined;
  }
};

const normalizeMessages = (messages: MessageWithMetadata[]): MessageWithMetadata[] => {
  return messages.map(message => {
    const rawCreatedAt = message.metadata?.createdAt;
    const createdAt = rawCreatedAt instanceof Date ? rawCreatedAt : new Date(rawCreatedAt ?? Date.now());
    const isValidDate = !Number.isNaN(createdAt.getTime());

    return {
      ...message,
      metadata: {
        ...message.metadata,
        createdAt: isValidDate ? createdAt : new Date(),
      },
    };
  });
};

// Helper functions for managing chat messages in localStorage
export const ChatMessagesStorage = {
  getAll: (): Record<string, MessageWithMetadata[]> => {
    try {
      const stored = localStorage.getItem(CHAT_MESSAGES_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  },

  get: (chatId: string): MessageWithMetadata[] => {
    const allMessages = ChatMessagesStorage.getAll();
    return normalizeMessages(allMessages[chatId] || []);
  },

  save: (chatId: string, messages: MessageWithMetadata[]): void => {
    try {
      const allMessages = ChatMessagesStorage.getAll();
      allMessages[chatId] = messages;
      localStorage.setItem(CHAT_MESSAGES_KEY, JSON.stringify(allMessages));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  },

  remove: (chatId: string): void => {
    try {
      const allMessages = ChatMessagesStorage.getAll();
      delete allMessages[chatId];
      localStorage.setItem(CHAT_MESSAGES_KEY, JSON.stringify(allMessages));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  },
};

// Helper functions for managing chat list in localStorage
export const ChatListStorage = {
  get: (userChatKey: string): ChatInfo[] => {
    try {
      const stored = localStorage.getItem(`${CHAT_LIST_KEY}-${userChatKey}`);
      if (!stored) return [];

      const parsed = JSON.parse(stored) as ChatInfo[];
      return parsed;
    } catch {
      return [];
    }
  },

  save: (userChatKey: string, chats: ChatInfo[]): void => {
    try {
      localStorage.setItem(`${CHAT_LIST_KEY}-${userChatKey}`, JSON.stringify(chats));
    } catch {
      // Silently fail if localStorage is unavailable
    }
  },

  clear: (userChatKey: string): void => {
    try {
      localStorage.removeItem(`${CHAT_LIST_KEY}-${userChatKey}`);
    } catch {
      // Silently fail if localStorage is unavailable
    }
  },
};
